/* eslint-disable */
/* eslint-disable @typescript-eslint/no-unused-vars, no-useless-escape, no-mixed-operators, react-hooks/exhaustive-deps, jsx-a11y/img-redundant-alt */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { CustomXAxisTick } from '../../components/CustomXAxisTick';
import '../../styles/components.css';
import { useAuth } from '../../contexts/AuthContext';

interface Project {
  id: string;
  name: string;
  summary: string;
  company_id: string;
  status: string;
  implementation_type?: 'ngo_led' | 'direct';
}

interface Metric {
  id: string;
  project_id: string;
  metric_name: string;
  target_value: number;
  current_value?: number;
  baseline_value?: number;
  unit: string;
}

interface QuarterlyReport {
  id: string;
  project_id: string;
  quarter: string;
  year: number;
  narrative_summary: string;
  photos?: string[];
  videos?: string[];
  beneficiary_stories?: any[];
  created_at: string;
  projects?: { name: string };
  achieved_values?: { [key: string]: number };
}

interface Evidence {
  id: string;
  project_id: string;
  evidence_data: any[];
  created_at: string;
  projects?: { name: string };
}

interface FinalReport {
  id: string;
  project_id: string;
  final_kpi_values: any;
  outcome_narrative: string;
  beneficiary_reach: number;
  photos?: string[];
  report_pdf_url?: string;
  created_at: string;
  projects?: { name: string };
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

// Helper function to get signed URL for viewing/downloading files
const getSignedUrl = async (url: string, bucket: string = 'fund-request-attachments'): Promise<string> => {
  try {
    if (!url) return '';
    if (!url.startsWith('http')) return url;

    const buckets = [bucket, 'ngo-documents', 'fund-request-attachments'];
    const uniqueBuckets = Array.from(new Set(buckets));

    for (const bucketName of uniqueBuckets) {
      if (url.includes(`/${bucketName}/`)) {
        const parts = url.split(`/${bucketName}/`);
        if (parts.length > 1) {
          const filePath = decodeURIComponent(parts[1].split('?')[0]);
          const { data, error } = await supabase.storage
            .from(bucketName)
            .createSignedUrl(filePath, 3600);
          if (!error && data?.signedUrl) return data.signedUrl;
        }
      }
    }

    // Fallback search
    for (const bucketName of uniqueBuckets) {
       const { data: list } = await supabase.storage.from(bucketName).list();
       // This is expensive, better to just return url if detection fails
    }

    return url;
  } catch (err) {
    return url;
  }
};

const SignedImage: React.FC<{ url: string; alt?: string; style?: React.CSSProperties; className?: string; onClick?: () => void; onError?: (e: any) => void }> = ({ url, alt, style, className, onClick, onError }) => {
  const [signedUrl, setSignedUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      let sUrl = await getSignedUrl(url, 'fund-request-attachments');
      if (sUrl === url) sUrl = await getSignedUrl(url, 'ngo-documents');
      setSignedUrl(sUrl);
      setLoading(false);
    };
    load();
  }, [url]);

  if (loading) return <div style={{ ...style, background: '#f3f4f6' }} className={className} />;
  
  return (
    <img
      src={signedUrl}
      alt={alt}
      style={style}
      className={className}
      onClick={onClick}
      onError={async (e) => {
        if (signedUrl.includes('fund-request-attachments')) {
          const fallback = await getSignedUrl(url, 'ngo-documents');
          (e.target as HTMLImageElement).src = fallback;
        }
      }}
    />
  );
};

const CompanyImpact: React.FC = () => {
  const { userProfile } = useAuth();
  const companyId = userProfile?.company_id;
  // Allow fallback table names if schemas differ
  const evidenceTables = useRef(['project_evidence', 'project_evidences']);
  const finalReportTables = useRef(['final_impact_reports', 'final_reports']);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [projects, setProjects] = useState<Project[]>([]);
  const [metrics, setMetrics] = useState<Metric[]>([]);
  const [quarterlyReports, setQuarterlyReports] = useState<QuarterlyReport[]>([]);
  const [evidence, setEvidence] = useState<Evidence[]>([]);
  const [finalReports, setFinalReports] = useState<FinalReport[]>([]);
  const [activeTab, setActiveTab] = useState<'direct' | 'reports' | 'evidence' | 'photos' | 'final'>('direct');
  const [imageUrlCache, setImageUrlCache] = useState<{ [key: string]: string }>({});
  const [selectedPhotoDetails, setSelectedPhotoDetails] = useState<any>(null);
  const [updatingMetric, setUpdatingMetric] = useState<{ id: string, name: string, value: string, project_id: string } | null>(null);
  const [isUpdatingMetric, setIsUpdatingMetric] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [submittingReport, setSubmittingReport] = useState(false);
  const [selectedProjectForReport, setSelectedProjectForReport] = useState<Project | null>(null);
  const [beneficiaryStories, setBeneficiaryStories] = useState<{ story: string; name?: string }[]>([]);
  const [reportForm, setReportForm] = useState({
    quarter: 'Q1',
    year: new Date().getFullYear(),
    narrative_summary: '',
    photos: [] as File[],
    videos: [] as File[],
    metric_updates: {} as { [key: string]: string }
  });

  useEffect(() => {
    if (companyId) {
      loadData();
    }
  }, [companyId]);

  // Pre-load signed URLs for all images when evidence or final reports change
  useEffect(() => {
    const loadImageUrls = async () => {
      const urlCache: { [key: string]: string } = { ...imageUrlCache };
      let hasNewUrls = false;

      // Load URLs for evidence photos
      for (const ev of evidence) {
        for (const item of ev.evidence_data || []) {
          if (item.type === 'photo' && item.url && !urlCache[item.url]) {
            try {
              const signedUrl = await getSignedUrl(item.url, 'ngo-documents');
              urlCache[item.url] = signedUrl;
              hasNewUrls = true;
            } catch (err) {
              // Keep original URL if signed URL fails
              urlCache[item.url] = item.url;
              hasNewUrls = true;
            }
          }
          if (item.type === 'video' && item.url && !urlCache[item.url]) {
            try {
              const signedUrl = await getSignedUrl(item.url, 'ngo-documents');
              urlCache[item.url] = signedUrl;
              hasNewUrls = true;
            } catch (err) {
              urlCache[item.url] = item.url;
              hasNewUrls = true;
            }
          }
        }
      }

      // Load URLs for final report photos
      for (const report of finalReports) {
        if (report.photos) {
          for (const photo of report.photos) {
            if (photo && !urlCache[photo]) {
              try {
                const signedUrl = await getSignedUrl(photo, 'ngo-documents');
                urlCache[photo] = signedUrl;
                hasNewUrls = true;
              } catch (err) {
                urlCache[photo] = photo;
                hasNewUrls = true;
              }
            }
          }
        }
      }

      // Load URLs for quarterly report photos and videos
      for (const report of quarterlyReports) {
        if (report.photos) {
          for (const photo of report.photos) {
            if (photo && !urlCache[photo]) {
              try {
                const signedUrl = await getSignedUrl(photo, 'ngo-documents');
                urlCache[photo] = signedUrl;
                hasNewUrls = true;
              } catch (err) {
                urlCache[photo] = photo;
                hasNewUrls = true;
              }
            }
          }
        }
        if (report.videos) {
          for (const video of report.videos) {
            if (video && !urlCache[video]) {
              try {
                const signedUrl = await getSignedUrl(video, 'ngo-documents');
                urlCache[video] = signedUrl;
                hasNewUrls = true;
              } catch (err) {
                urlCache[video] = video;
                hasNewUrls = true;
              }
            }
          }
        }
      }

      // Only update state if we have new URLs to avoid unnecessary re-renders
      if (hasNewUrls) {
        setImageUrlCache(urlCache);
      }
    };

    if (evidence.length > 0 || finalReports.length > 0 || quarterlyReports.length > 0) {
      loadImageUrls();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evidence.length, finalReports.length, quarterlyReports.length]);

  const loadData = async () => {
    if (!companyId) return;

    setLoading(true);
    setError('');

    try {
      // 1. Fetch Projects
      const { data: projectsData, error: projectsError } = await supabase
        .from('projects')
        .select('id, name, summary, company_id, status, implementation_type')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (projectsError) throw projectsError;
      setProjects(projectsData || []);

      const projectIds = (projectsData || []).map((p: any) => p.id);

      if (projectIds.length > 0) {
        // 2. Fetch Metrics (Definitions + Progress)
        const { data: metricDefs, error: defsError } = await supabase
          .from('project_metrics')
          .select('id, project_id, metric_name, target_value, unit')
          .in('project_id', projectIds);

        const { data: metricProgs } = await supabase
          .from('project_metrics_progress')
          .select('project_id, metric_name, current_value')
          .in('project_id', projectIds);

        if (!defsError && metricDefs) {
          const mergedMetrics: Metric[] = (metricDefs as any).map((def: any) => {
            const prog = metricProgs?.find((p: any) =>
              p.project_id === def.project_id &&
              p.metric_name === def.metric_name
            );
            return {
              id: def.id,
              project_id: def.project_id,
              metric_name: def.metric_name,
              target_value: def.target_value,
              unit: def.unit,
              baseline_value: 0,
              current_value: prog ? prog.current_value : 0
            };
          });
          setMetrics(mergedMetrics);
        }

        // 3. Fetch Quarterly Reports
        const { data: reportsData, error: reportsError } = await supabase
          .from('quarterly_progress')
          .select(`
            id, project_id, metric_id, quarter, year, achieved_value, notes, created_at,
            projects:project_id ( name ),
            project_metrics:metric_id ( metric_name, unit )
          `)
          .in('project_id', projectIds)
          .not('metric_id', 'is', null)
          .order('created_at', { ascending: false });

        if (!reportsError && reportsData) {
          const reportMap: { [key: string]: any } = {};
          reportsData.forEach((r: any) => {
            const key = `${r.project_id}_${r.quarter}_${r.year}`;
            if (!reportMap[key]) {
              let reportData: any = {};
              try { reportData = typeof r.notes === 'string' ? JSON.parse(r.notes) : (r.notes || {}); } catch { reportData = {}; }

              reportMap[key] = {
                id: r.id,
                project_id: r.project_id,
                quarter: r.quarter,
                year: r.year,
                narrative_summary: reportData.narrative_summary || '',
                photos: reportData.photos || [],
                videos: reportData.videos || [],
                beneficiary_stories: reportData.beneficiary_stories || [],
                created_at: r.created_at,
                projects: Array.isArray(r.projects) && r.projects.length > 0 ? r.projects[0] : { name: 'Project' },
                achieved_values: {}
              };
            }
            if (r.metric_id) {
              reportMap[key].achieved_values[r.metric_id] = r.achieved_value;
            }
          });
          setQuarterlyReports(Object.values(reportMap));
        }

        // 4. Fetch Evidence (with table fallback)
        let evidenceData: any[] | null = null;
        for (const tableName of evidenceTables.current) {
          const { data, error } = await supabase
            .from(tableName)
            .select(`
              id, project_id, evidence_data, created_at
            `)
            .in('project_id', projectIds)
            .order('created_at', { ascending: false });

          if (!error) {
            evidenceData = data || [];
            break;
          }
          if (!`${error.message}`.toLowerCase().includes('does not exist')) {
            throw error;
          }
        }

        if (evidenceData) {
          const mappedEvidence: Evidence[] = evidenceData.map((e: any) => {
            const evidenceArray = typeof e.evidence_data === 'string'
              ? (() => { try { return JSON.parse(e.evidence_data || '[]'); } catch { return []; } })()
              : (e.evidence_data || []);
            const projectName = projects.find(p => p.id === e.project_id)?.name || 'Project';
            return {
              id: e.id,
              project_id: e.project_id,
              evidence_data: evidenceArray,
              created_at: e.created_at,
              projects: { name: projectName }
            };
          });
          setEvidence(mappedEvidence);
        }

        // 5. Fetch Final Reports (with table fallback)
        let finalData: any[] | null = null;
        for (const tableName of finalReportTables.current) {
          const { data, error } = await supabase
            .from(tableName)
            .select(`
              id, project_id, final_kpi_values, outcome_narrative, beneficiary_reach,
              photos, report_pdf_url, created_at
            `)
            .in('project_id', projectIds)
            .order('created_at', { ascending: false });

          if (!error) {
            finalData = data || [];
            break;
          }
          if (!`${error.message}`.toLowerCase().includes('does not exist')) {
            throw error;
          }
        }

        if (finalData) {
          const mappedFinal: FinalReport[] = finalData.map((f: any) => {
            const finalValues = typeof f.final_kpi_values === 'string'
              ? (() => { try { return JSON.parse(f.final_kpi_values || '{}'); } catch { return {}; } })()
              : (f.final_kpi_values || {});
            const photosArray = Array.isArray(f.photos) ? f.photos : [];
            const projectName = projects.find(p => p.id === f.project_id)?.name || 'Project';
            return {
              id: f.id,
              project_id: f.project_id,
              final_kpi_values: finalValues,
              outcome_narrative: f.outcome_narrative || '',
              beneficiary_reach: f.beneficiary_reach || 0,
              photos: photosArray,
              report_pdf_url: f.report_pdf_url,
              created_at: f.created_at,
              projects: { name: projectName }
            };
          });
          setFinalReports(mappedFinal);
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load impact data');
    } finally {
      setLoading(false);
    }
  };

  const syncMetricProgress = async (projectId: string) => {
    try {
      const { data: allProgress, error: progressError } = await supabase
        .from('quarterly_progress')
        .select('metric_id, achieved_value')
        .eq('project_id', projectId);

      if (progressError) throw progressError;

      const cumulativeValues: { [metricId: string]: number } = {};
      (allProgress || []).forEach((record: any) => {
        if (record.metric_id) {
          cumulativeValues[record.metric_id] = (cumulativeValues[record.metric_id] || 0) + (Number(record.achieved_value) || 0);
        }
      });

      const { data: metricDefs, error: defError } = await supabase
        .from('project_metrics')
        .select('id, metric_name, target_value, unit')
        .eq('project_id', projectId);

      if (defError) throw defError;

      for (const def of (metricDefs || [])) {
        const cumulativeValue = cumulativeValues[def.id] || 0;
        const { data: existingProgress } = await supabase
          .from('project_metrics_progress')
          .select('id')
          .eq('project_id', projectId)
          .eq('metric_name', def.metric_name)
          .maybeSingle();

        if (existingProgress) {
          await supabase
            .from('project_metrics_progress')
            .update({
              current_value: cumulativeValue,
              updated_at: new Date().toISOString()
            })
            .eq('id', existingProgress.id);
        } else {
          await supabase
            .from('project_metrics_progress')
            .insert({
              project_id: projectId,
              metric_name: def.metric_name,
              target_value: def.target_value,
              current_value: cumulativeValue,
              unit: def.unit,
              updated_at: new Date().toISOString()
            });
        }
      }
    } catch (err) {
      console.error('Error syncing:', err);
    }
  };

  const loadExistingReportValues = async (project: Project | null, quarter?: string, year?: number) => {
    const targetProject = project || selectedProjectForReport;
    if (!targetProject) return;

    const projectMetrics = metrics.filter(m => m.project_id === targetProject.id);
    const newQuarter = quarter || reportForm.quarter;
    const newYear = year || reportForm.year;

    try {
      const { data: existingReports } = await supabase
        .from('quarterly_progress')
        .select('metric_id, achieved_value, notes')
        .eq('project_id', targetProject.id)
        .eq('quarter', newQuarter)
        .eq('year', newYear);

      if (existingReports && existingReports.length > 0) {
        const existingValues: { [key: string]: string } = {};
        let loadedNarrative = '';
        let loadedStories: { story: string; name?: string }[] = [];

        // Get data from first report row
        const firstReport = existingReports[0];
        if (firstReport.notes) {
          try {
            const notesData = typeof firstReport.notes === 'string' ? JSON.parse(firstReport.notes) : firstReport.notes;
            loadedNarrative = notesData.narrative_summary || '';
            loadedStories = notesData.beneficiary_stories || [];
          } catch (e) {
            console.error('Error parsing notes:', e);
          }
        }

        projectMetrics.forEach(m => {
          const existing = existingReports.find(r => r.metric_id === m.id);
          existingValues[m.id] = existing ? String(existing.achieved_value) : '0';
        });

        setReportForm(prev => ({
          ...prev,
          quarter: newQuarter,
          year: newYear,
          narrative_summary: loadedNarrative,
          metric_updates: existingValues
        }));
        setBeneficiaryStories(loadedStories || []);
      } else {
        // No existing report for this period, reset
        const freshValues: { [key: string]: string } = {};
        projectMetrics.forEach(m => { freshValues[m.id] = '0'; });
        setReportForm(prev => ({
          ...prev,
          quarter: newQuarter,
          year: newYear,
          narrative_summary: '',
          metric_updates: freshValues
        }));
        setBeneficiaryStories([]);
      }
    } catch (err) {
      console.error('Error loading existing report:', err);
    }
  };

  const handleReportSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProjectForReport || !companyId) return;

    setSubmittingReport(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const photoUrls: string[] = [];
      for (const photo of reportForm.photos) {
        const fileName = `direct_reports/${selectedProjectForReport.id}/${Date.now()}_${photo.name}`;
        const { error: uploadError } = await supabase.storage.from('fund-request-attachments').upload(fileName, photo);
        if (!uploadError) {
          const { data: { publicUrl } } = supabase.storage.from('fund-request-attachments').getPublicUrl(fileName);
          photoUrls.push(publicUrl);
        }
      }

      const videoUrls: string[] = [];
      for (const video of reportForm.videos) {
        const fileName = `direct_reports/${selectedProjectForReport.id}/${Date.now()}_${video.name}`;
        const { error: uploadError } = await supabase.storage.from('fund-request-attachments').upload(fileName, video);
        if (!uploadError) {
          const { data: { publicUrl } } = supabase.storage.from('fund-request-attachments').getPublicUrl(fileName);
          videoUrls.push(publicUrl);
        }
      }

      const projectMetrics = metrics.filter(m => m.project_id === selectedProjectForReport.id);
      
      for (const metric of projectMetrics) {
        const achievedValue = Number(reportForm.metric_updates[metric.id]) || 0;
        const notes = {
          narrative_summary: reportForm.narrative_summary,
          photos: photoUrls,
          videos: videoUrls,
          beneficiary_stories: beneficiaryStories
        };

        const { error: progressError } = await supabase
          .from('quarterly_progress')
          .upsert({
            project_id: selectedProjectForReport.id,
            metric_id: metric.id, // This is the ID from project_metrics
            quarter: reportForm.quarter,
            year: reportForm.year,
            achieved_value: achievedValue,
            notes: JSON.stringify(notes),
            submitted_by: user.id
          }, { onConflict: 'project_id,metric_id,quarter,year' });

        if (progressError) throw progressError;
      }

      await syncMetricProgress(selectedProjectForReport.id);
      alert('Impact report submitted successfully!');
      // Reset form
      setReportForm({
        quarter: 'Q1',
        year: new Date().getFullYear(),
        narrative_summary: '',
        photos: [],
        videos: [],
        metric_updates: {}
      });
      setBeneficiaryStories([]);
      setShowReportModal(false);
      setSelectedProjectForReport(null);
      await loadData();
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    } finally {
      setSubmittingReport(false);
    }
  };

  const handleUpdateMetric = async () => {
    if (!updatingMetric || isNaN(Number(updatingMetric.value))) return;

    setIsUpdatingMetric(true);
    try {
      const { error } = await supabase
        .from('project_metrics_progress')
        .upsert({
          project_id: updatingMetric.project_id,
          metric_name: updatingMetric.name,
          current_value: Number(updatingMetric.value),
          updated_at: new Date().toISOString()
        }, { onConflict: 'project_id,metric_name' });

      if (error) throw error;

      await loadData();
      setUpdatingMetric(null);
      alert('Metric updated successfully!');
    } catch (err: any) {
      console.error('Error updating metric:', err);
      alert(err.message || 'Failed to update metric');
    } finally {
      setIsUpdatingMetric(false);
    }
  };

  // --- Calculations for Dashboard ---
  const overviewStats = useMemo(() => {
    const activeProjects = projects.filter(p => p.status === 'active').length;
    const totalMetrics = metrics.length;
    const completedMetrics = metrics.filter(m => (m.current_value || 0) >= m.target_value).length;
    const totalBeneficiaries = finalReports.reduce((sum, r) => sum + (r.beneficiary_reach || 0), 0);
    const avgProgress = metrics.length > 0
      ? metrics.reduce((sum, m) => {
        const progress = m.target_value > 0 ? ((m.current_value || 0) / m.target_value) * 100 : 0;
        return sum + Math.min(progress, 100);
      }, 0) / metrics.length
      : 0;

    return {
      totalProjects: projects.length,
      activeProjects,
      totalMetrics,
      completedMetrics,
      totalBeneficiaries,
      avgProgress: Math.round(avgProgress)
    };
  }, [projects, metrics, finalReports]);

  // Graph Data: Project-wise Progress
  const projectChartData = useMemo(() => {
    const projectMap: { [key: string]: { name: string; target: number; achieved: number } } = {};

    metrics.forEach(m => {
      const project = projects.find(p => p.id === m.project_id);
      if (project) {
        if (!projectMap[project.id]) {
          projectMap[project.id] = {
            name: project.name.length > 20 ? project.name.substring(0, 20) + '...' : project.name,
            target: 0,
            achieved: 0
          };
        }
        projectMap[project.id].target += m.target_value;
        projectMap[project.id].achieved += (m.current_value || 0);
      }
    });

    return Object.values(projectMap);
  }, [metrics, projects]);

  // Collect all photos/videos for Photos tab
  const allPhotos: any[] = [];
  const allVideos: any[] = [];

  quarterlyReports.forEach(r => {
    (r.photos || []).forEach(p => allPhotos.push({ url: p, project: r.projects?.name, source: `Quarterly Report - ${r.quarter} ${r.year}` }));
    (r.videos || []).forEach(v => allVideos.push({ url: v, project: r.projects?.name, source: `Quarterly Report - ${r.quarter} ${r.year}` }));
  });
  evidence.forEach(e => {
    (e.evidence_data || []).forEach((item: any) => {
      if (item.type === 'photo' && item.url) allPhotos.push({
        url: item.url,
        project: e.projects?.name,
        source: 'Evidence Upload',
        caption: item.caption,
        location_tags: item.location_tags,
        consent_confirmation: item.consent_confirmation,
        uploaded_date: e.created_at
      });
      if (item.type === 'video' && item.url) allVideos.push({
        url: item.url,
        project: e.projects?.name,
        source: 'Evidence Upload',
        caption: item.caption,
        location_tags: item.location_tags,
        consent_confirmation: item.consent_confirmation,
        uploaded_date: e.created_at
      });
    });
  });
  finalReports.forEach(r => {
    (r.photos || []).forEach(p => allPhotos.push({ url: p, project: r.projects?.name, source: 'Final Impact Report', caption: r.outcome_narrative }));
  });

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h1 style={{ margin: 0 }}>Impact Dashboard</h1>
      </div>

      {error && (
        <div className="toast toast-error" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      <div style={{
        backgroundColor: '#eff6ff',
        border: '1px solid #bfdbfe',
        color: '#1e3a8a',
        padding: '1rem 1.5rem',
        borderRadius: '8px',
        fontSize: '0.9rem',
        marginBottom: '2rem',
        lineHeight: '1.6'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <span style={{ fontSize: '1.25rem' }}>💡</span>
          <strong style={{ fontSize: '1rem' }}>Impact Workflow Guide</strong>
        </div>
        <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
          <li><strong>Direct Implementation:</strong> Report on KPIs and add supporting evidence for direct projects.</li>
          <li><strong>Quarterly Reports:</strong> View quarterly progress submitted by NGOs.</li>
          <li><strong>Evidence & Stories:</strong> Review media evidence and beneficiary stories.</li>
          <li><strong>Photos & Videos:</strong> Access all visual proof organized by project.</li>
          <li><strong>Final Reports:</strong> Review comprehensive project closure and outcome reports.</li>
        </ul>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '2rem', borderBottom: '2px solid var(--border)' }}>
        {(['direct', 'reports', 'evidence', 'photos', 'final'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '0.75rem 1.5rem',
              border: 'none',
              background: 'transparent',
              borderBottom: activeTab === tab ? '2px solid var(--primary)' : '2px solid transparent',
              color: activeTab === tab ? 'var(--primary)' : 'var(--text-secondary)',
              fontWeight: activeTab === tab ? '600' : '400',
              cursor: 'pointer',
              textTransform: 'capitalize',
              marginBottom: '-2px'
            }}
          >
            {tab === 'direct' ? 'Direct Implementation' :
                tab === 'reports' ? 'Quarterly Reports' :
                  tab === 'evidence' ? 'Evidence & Stories' :
                    tab === 'photos' ? 'Photos & Videos' : 'Final Reports'}
          </button>
        ))}
      </div>

      {/* Direct Implementation Tab */}
      {activeTab === 'direct' && (
        <div>
          <div className="card" style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2 style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>Quarterly / Milestone Impact Reporting</h2>
              <p style={{ color: 'var(--muted)' }}>Report on each KPI and add supporting evidence and stories.</p>
            </div>
            <button 
              className="btn btn-primary"
              onClick={() => {
                setSelectedProjectForReport(null);
                setReportForm({
                  quarter: 'Q1',
                  year: new Date().getFullYear(),
                  narrative_summary: '',
                  photos: [],
                  videos: [],
                  metric_updates: {}
                });
                setBeneficiaryStories([]);
                setShowReportModal(true);
              }}
              style={{ background: '#4d905e', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '8px', fontWeight: '500' }}
            >
              + Submit Quarterly Report
            </button>
          </div>

          <h3 style={{ marginBottom: '1.5rem' }}>Submitted Reports</h3>
          {quarterlyReports.filter(r => (projects.find(p => p.id === r.project_id)?.implementation_type === 'direct')).length === 0 ? (
            <div className="card" style={{ padding: '3rem', textAlign: 'center', color: 'var(--muted)' }}>
              <p>No quarterly reports submitted yet for direct projects.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '1.5rem' }}>
              {quarterlyReports
                .filter(r => (projects.find(p => p.id === r.project_id)?.implementation_type === 'direct'))
                .sort((a,b) => b.year - a.year || b.quarter.localeCompare(a.quarter))
                .map((report) => (
                <div key={report.id} className="card" style={{ padding: '2rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1.5rem' }}>
                    <div>
                      <h4 style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>{report.projects?.name || projects.find(p => p.id === report.project_id)?.name}</h4>
                      <p style={{ color: 'var(--muted)', fontSize: '0.9rem' }}>
                        {report.quarter} {report.year} • {new Date(report.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div style={{ display: 'flex', gap: '0.75rem' }}>
                      <button className="btn btn-sm btn-outline" style={{ color: '#ef4444', borderColor: '#ef4444', padding: '0.4rem 1rem' }}>Delete</button>
                    </div>
                  </div>

                  {report.narrative_summary && (
                    <div style={{ marginBottom: '1.5rem' }}>
                      <strong style={{ display: 'block', marginBottom: '0.5rem' }}>Summary:</strong>
                      <p style={{ color: '#4b5563', lineHeight: '1.6' }}>{report.narrative_summary}</p>
                    </div>
                  )}

                  <div className="table-container" style={{ marginBottom: '1.5rem', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                    <table className="table" style={{ margin: 0 }}>
                      <thead>
                        <tr style={{ background: '#f9fafb' }}>
                          <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>METRIC</th>
                          <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>ACHIEVED VALUE</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(report.achieved_values || {}).map(([mId, val]) => {
                          const metric = metrics.find(m => m.id === mId || (m as any).metric_definition_id === mId);
                          if (!metric) return null;
                          return (
                            <tr key={mId}>
                              <td style={{ padding: '1rem' }}><strong>{metric.metric_name}</strong></td>
                              <td style={{ padding: '1rem' }}>{Number(val).toLocaleString('en-IN')} {metric.unit}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {report.photos && report.photos.length > 0 && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '1rem' }}>
                      {report.photos.map((p, idx) => (
                        <SignedImage 
                          key={idx} 
                          url={p} 
                          style={{ width: '100%', height: '120px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #e5e7eb' }} 
                        />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {activeTab === 'reports' && (
        <div style={{ display: 'grid', gap: '1.5rem' }}>
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <h2>Quarterly / Milestone Impact Reports</h2>
            <p style={{ color: 'var(--muted)' }}>Review periodic reports with progress on KPIs and evidence submitted by NGOs.</p>
          </div>

          {quarterlyReports.length === 0 ? (
            <div className="card" style={{ padding: '3rem', textAlign: 'center' }}>
              <p style={{ color: 'var(--muted)' }}>No quarterly reports submitted yet.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '1.5rem' }}>
              {quarterlyReports.map((report) => (
                <div key={report.id} className="card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
                    <div>
                      <h3>{report.projects?.name || 'Project'}</h3>
                      <p style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>
                        {report.quarter} {report.year} • {new Date(report.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Narrative Summary:</strong>
                    <p style={{ marginTop: '0.5rem', whiteSpace: 'pre-wrap' }}>{report.narrative_summary || 'No narrative provided'}</p>
                  </div>

                  {/* Display metrics progress */}
                  {(report as any).metrics && (report as any).metrics.length > 0 && (
                    <div style={{ marginBottom: '1rem' }}>
                      <strong>Metrics Progress:</strong>
                      <div className="table-container" style={{ marginTop: '0.5rem' }}>
                        <table className="table">
                          <thead>
                            <tr>
                              <th>Metric</th>
                              <th>Achieved Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(report as any).metrics.map((m: any, idx: number) => (
                              <tr key={idx}>
                                <td><strong>{m.metric_name}</strong> {m.unit ? `(${m.unit})` : ''}</td>
                                <td>{m.achieved_value?.toLocaleString('en-IN') || '0'} {m.unit || ''}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {report.beneficiary_stories && report.beneficiary_stories.length > 0 && (
                    <div style={{ marginBottom: '1rem' }}>
                      <strong>Beneficiary Stories:</strong>
                      {report.beneficiary_stories.map((story: any, idx: number) => (
                        <div key={idx} style={{ marginTop: '0.5rem', padding: '0.75rem', background: '#f9fafb', borderRadius: '8px' }}>
                          {story.name && <strong>{story.name}: </strong>}
                          <span>{story.story}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Display Photos */}
                  {report.photos && report.photos.length > 0 && (
                    <div style={{ marginBottom: '1rem' }}>
                      <strong>Photos:</strong>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem', marginTop: '0.5rem' }}>
                        {report.photos.map((photo: string, idx: number) => (
                          <div key={idx} style={{ position: 'relative' }}>
                            <SignedImage
                              url={photo}
                              alt="Quarterly Evidence"
                              style={{
                                width: '100%',
                                height: '150px',
                                objectFit: 'cover',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                border: '1px solid #e5e7eb'
                              }}
                              onClick={() => {
                                 window.open(photo, '_blank');
                              }}
                            />
                            <a
                              href={imageUrlCache[photo] || photo}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                display: 'block',
                                marginTop: '0.25rem',
                                fontSize: '0.875rem',
                                color: 'var(--primary)',
                                textAlign: 'center'
                              }}
                              onClick={async (e) => {
                                e.preventDefault();
                                const urlToOpen = imageUrlCache[photo] || await getSignedUrl(photo, 'ngo-documents');
                                window.open(urlToOpen, '_blank');
                              }}
                            >
                              View Full Size
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Display Videos */}
                  {report.videos && report.videos.length > 0 && (
                    <div style={{ marginBottom: '1rem' }}>
                      <strong>Videos:</strong>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                        {report.videos.map((video: string, idx: number) => (
                          <a
                            key={idx}
                            href={video}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-outline"
                            onClick={async (e) => {
                              e.preventDefault();
                              const signedUrl = await getSignedUrl(video, 'ngo-documents');
                              window.open(signedUrl, '_blank');
                            }}
                          >
                            🎥 Video {idx + 1}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Evidence & Stories Tab */}
      {activeTab === 'evidence' && (
        <div>
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <h2>Evidence Review & Story Curation</h2>
            <p style={{ color: 'var(--muted)', marginBottom: '1rem' }}>
              Review photo/story uploads for communication use.
            </p>
          </div>

          {evidence.length === 0 ? (
            <div className="card" style={{ padding: '3rem', textAlign: 'center' }}>
              <p style={{ color: 'var(--muted)' }}>No evidence uploaded yet.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '1.5rem' }}>
              {evidence.map((ev) => (
                <div key={ev.id} className="card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
                    <div>
                      <h3>{ev.projects?.name || 'Project'}</h3>
                      <p style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>
                        Uploaded on {new Date(ev.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <button
                      className="btn btn-sm"
                      style={{
                        fontSize: '0.8rem',
                        backgroundColor: '#ef4444',
                        color: 'white',
                        border: 'none'
                      }}
                      onClick={async () => {
                        if (!window.confirm('Are you sure you want to delete this evidence upload? This will delete all photos, videos, and stories in this upload.')) return;
                        try {
                          // Find which evidence table this came from
                          const evidenceTables = ['project_evidence', 'project_evidences'];
                          let deleted = false;

                          for (const tableName of evidenceTables) {
                            const { error } = await supabase
                              .from(tableName)
                              .delete()
                              .eq('id', ev.id);

                            if (!error) {
                              deleted = true;
                              break;
                            }
                          }

                          if (!deleted) {
                            throw new Error('Failed to delete evidence');
                          }

                          // Delete files from storage
                          const evidenceData = Array.isArray(ev.evidence_data)
                            ? ev.evidence_data
                            : (typeof ev.evidence_data === 'string'
                              ? JSON.parse(ev.evidence_data || '[]')
                              : []);

                          for (const item of evidenceData) {
                            if (item.url) {
                              let pathInBucket = item.url;
                              if (item.url.includes('/storage/v1/object/public/')) {
                                const parts = item.url.split('/storage/v1/object/public/');
                                if (parts.length > 1) {
                                  pathInBucket = parts[1].replace('ngo-documents/', '');
                                }
                              } else if (item.url.includes('/storage/v1/object/sign/')) {
                                const parts = item.url.split('/storage/v1/object/sign/');
                                if (parts.length > 1) {
                                  const pathParts = parts[1].split('/');
                                  if (pathParts.length > 1) {
                                    pathInBucket = pathParts.slice(1).join('/');
                                  }
                                }
                              }

                              if (pathInBucket && pathInBucket !== item.url) {
                                await supabase.storage
                                  .from('ngo-documents')
                                  .remove([pathInBucket]);
                              }
                            }
                          }

                          alert('Evidence deleted successfully!');
                          window.location.reload(); // Refresh to update the list
                        } catch (err: any) {
                          console.error('Error deleting evidence:', err);
                          alert(`Error: ${err.message || 'Failed to delete evidence'}`);
                        }
                      }}
                    >
                      Delete
                    </button>
                  </div>

                  <div style={{ display: 'grid', gap: '1rem' }}>
                    {ev.evidence_data.map((item: any, idx: number) => (
                      <div key={idx} style={{ padding: '1rem', background: '#f9fafb', borderRadius: '8px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.5rem' }}>
                          <strong style={{ textTransform: 'capitalize' }}>{item.type}</strong>
                          {item.location_tags && (
                            <span style={{ fontSize: '0.875rem', color: 'var(--muted)' }}>
                              📍 {item.location_tags}
                            </span>
                          )}
                        </div>
                        {item.caption && (
                          <p style={{ marginBottom: '0.5rem' }}>{item.caption}</p>
                        )}

                        {item.type === 'photo' && item.url ? (
                          <div style={{ marginTop: '0.5rem' }}>
                            <SignedImage
                              url={item.url}
                              alt={item.caption || 'Evidence'}
                              style={{
                                width: '100%',
                                maxHeight: '300px',
                                objectFit: 'contain',
                              }}
                            />
                          </div>
                        ) : item.url && (
                          <a
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-outline"
                            onClick={async (e) => {
                              e.preventDefault();
                              const signedUrl = await getSignedUrl(item.url, 'ngo-documents');
                              window.open(signedUrl, '_blank');
                            }}
                          >
                            View {item.type === 'video' ? 'Video' : 'Story'}
                          </a>
                        )}

                        {item.consent_confirmation && (
                          <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#10b981' }}>
                            ✓ Consent Confirmed
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Photos & Videos Tab - MODIFIED BIG VIEW DASHBOARD */}
      {activeTab === 'photos' && (
        <div>
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <h2>Photos & Videos</h2>
            <p style={{ color: 'var(--muted)', marginBottom: '1rem' }}>
              Aggregated gallery of all photos and videos from Quarterly Reports, Evidence, and Final Reports.
            </p>
          </div>

          {(allPhotos.length === 0 && allVideos.length === 0) ? (
            <div className="card" style={{ padding: '3rem', textAlign: 'center' }}>
              <p style={{ color: 'var(--muted)' }}>No photos or videos available.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '2rem' }}>

              {/* Render All Photos */}
              {allPhotos.map((photo, idx) => (
                <div key={`p-${idx}`} className="card" style={{ padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', transition: 'box-shadow 0.2s' }}>
                  <div style={{ position: 'relative', height: '220px', background: '#f3f4f6' }}>
                    <SignedImage
                      url={photo.url}
                      alt={photo.caption || 'Impact Photo'}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
                      onClick={() => {
                        window.open(photo.url, '_blank');
                      }}
                    />
                  </div>
                  <div style={{ padding: '1rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--primary)', fontWeight: 'bold', marginBottom: '0.25rem' }}>
                      📷 {photo.source}
                    </div>
                    <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', color: 'var(--text-primary)' }}>
                      {photo.project || 'Project'}
                    </h4>
                    {photo.caption && (
                      <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '1rem', flex: 1 }}>
                        {photo.caption.length > 80 ? photo.caption.substring(0, 80) + '...' : photo.caption}
                      </p>
                    )}
                    <div style={{ marginTop: 'auto', paddingTop: '1rem', borderTop: '1px solid var(--border)', display: 'flex', gap: '0.5rem', flexDirection: 'column' }}>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline"
                        style={{ width: '100%' }}
                        onClick={() => setSelectedPhotoDetails(photo)}
                      >
                        View Details
                      </button>
                      <a
                        href={imageUrlCache[photo.url] || photo.url}
                        target="_blank"
                        rel="noreferrer"
                        style={{ fontSize: '0.875rem', color: 'var(--primary)', textDecoration: 'none', fontWeight: '500', textAlign: 'center' }}
                        onClick={async (e) => {
                          e.preventDefault();
                          const urlToOpen = imageUrlCache[photo.url] || await getSignedUrl(photo.url, 'ngo-documents');
                          window.open(urlToOpen, '_blank');
                        }}
                      >
                        View Full Size &rarr;
                      </a>
                    </div>
                  </div>
                </div>
              ))}

              {/* Render All Videos */}
              {allVideos.map((video, idx) => (
                <div key={`v-${idx}`} className="card" style={{ padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', transition: 'box-shadow 0.2s' }}>
                  <div style={{ height: '220px', background: '#111827', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <a
                      href={imageUrlCache[video.url] || video.url}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: 'white', textDecoration: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                      onClick={async (e) => {
                        e.preventDefault();
                        const urlToOpen = imageUrlCache[video.url] || await getSignedUrl(video.url, 'ngo-documents');
                        window.open(urlToOpen, '_blank');
                      }}
                    >
                      <span style={{ fontSize: '3rem' }}>▶</span>
                      <span style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>Click to Play</span>
                    </a>
                  </div>
                  <div style={{ padding: '1rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--primary)', fontWeight: 'bold', marginBottom: '0.25rem' }}>
                      🎥 {video.source}
                    </div>
                    <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', color: 'var(--text-primary)' }}>
                      {video.project || 'Project'}
                    </h4>
                    {video.caption && (
                      <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '1rem', flex: 1 }}>
                        {video.caption.length > 80 ? video.caption.substring(0, 80) + '...' : video.caption}
                      </p>
                    )}
                    <div style={{ marginTop: 'auto', paddingTop: '1rem', borderTop: '1px solid var(--border)' }}>
                      <a
                        href={imageUrlCache[video.url] || video.url}
                        target="_blank"
                        rel="noreferrer"
                        style={{ fontSize: '0.875rem', color: 'var(--primary)', textDecoration: 'none', fontWeight: '500' }}
                        onClick={async (e) => {
                          e.preventDefault();
                          const urlToOpen = imageUrlCache[video.url] || await getSignedUrl(video.url, 'ngo-documents');
                          window.open(urlToOpen, '_blank');
                        }}
                      >
                        Watch Video &rarr;
                      </a>
                    </div>
                  </div>
                </div>
              ))}

            </div>
          )}
        </div>
      )}

      {/* Final Reports Tab */}
      {activeTab === 'final' && (
        <div>
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <h2>Final Impact Reports</h2>
            <p style={{ color: 'var(--muted)', marginBottom: '1rem' }}>
              Review final impact summaries and closure documents submitted by NGOs.
            </p>
          </div>

          {finalReports.length === 0 ? (
            <div className="card" style={{ padding: '3rem', textAlign: 'center' }}>
              <p style={{ color: 'var(--muted)' }}>No final reports submitted yet.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '1.5rem' }}>
              {finalReports.map((report) => (
                <div key={report.id} className="card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
                    <div>
                      <h3>{report.projects?.name || 'Project'}</h3>
                      <p style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>
                        Submitted on {new Date(report.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Beneficiary Reach:</strong>
                    <p style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--primary)' }}>
                      {report.beneficiary_reach.toLocaleString()}
                    </p>
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Final KPI Values:</strong>
                    <div className="table-container" style={{ marginTop: '0.5rem' }}>
                      <table className="table">
                        <thead>
                          <tr>
                            <th>Metric</th>
                            <th>Final Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(report.final_kpi_values || {}).map(([metricId, value]: [string, any]) => (
                            <tr key={metricId}>
                              <td>Metric {metricId}</td>
                              <td>{value}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Outcome Narrative:</strong>
                    <p style={{ marginTop: '0.5rem', whiteSpace: 'pre-wrap' }}>{report.outcome_narrative}</p>
                  </div>

                  {report.photos && report.photos.length > 0 && (
                    <div style={{ marginBottom: '1rem' }}>
                      <strong>Photos:</strong>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                        {report.photos.map((photo: string, idx: number) => (
                          <a
                            key={idx}
                            href={imageUrlCache[photo] || photo}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-outline"
                            onClick={async (e) => {
                              e.preventDefault();
                              const urlToOpen = imageUrlCache[photo] || await getSignedUrl(photo, 'ngo-documents');
                              window.open(urlToOpen, '_blank');
                            }}
                          >
                            📷 Photo {idx + 1}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {report.report_pdf_url && (
                    <div>
                      <a
                        href={report.report_pdf_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-primary"
                        onClick={async (e) => {
                          e.preventDefault();
                          const signedUrl = await getSignedUrl(report.report_pdf_url!, 'ngo-documents');
                          window.open(signedUrl, '_blank');
                        }}
                      >
                        📄 View Final Report PDF
                      </a>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Photo Details Modal */}
      {selectedPhotoDetails && (
        <div className="modal-overlay" onClick={() => setSelectedPhotoDetails(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2>Photo Details</h2>
              <button
                type="button"
                onClick={() => setSelectedPhotoDetails(null)}
                style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: 'var(--text-secondary)' }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <SignedImage
                url={selectedPhotoDetails.url}
                alt={selectedPhotoDetails.caption || 'Photo'}
                style={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: '500px',
                  objectFit: 'contain',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  backgroundColor: '#fff',
                  cursor: 'pointer'
                }}
                onClick={() => {
                  window.open(selectedPhotoDetails.url, '_blank');
                }}
              />
            </div>

            <div style={{ display: 'grid', gap: '1rem' }}>
              <div>
                <strong style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>Project</strong>
                <p style={{ marginTop: '0.25rem', fontSize: '1.1rem', fontWeight: '600' }}>
                  {selectedPhotoDetails.project || 'N/A'}
                </p>
              </div>

              <div>
                <strong style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>Source</strong>
                <p style={{ marginTop: '0.25rem' }}>
                  {selectedPhotoDetails.source || 'N/A'}
                </p>
              </div>

              {selectedPhotoDetails.caption && (
                <div>
                  <strong style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>Caption</strong>
                  <p style={{ marginTop: '0.25rem', whiteSpace: 'pre-wrap' }}>
                    {selectedPhotoDetails.caption}
                  </p>
                </div>
              )}

              {selectedPhotoDetails.location_tags && (
                <div>
                  <strong style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>Location</strong>
                  <p style={{ marginTop: '0.25rem' }}>
                    📍 {selectedPhotoDetails.location_tags}
                  </p>
                </div>
              )}

              {selectedPhotoDetails.uploaded_date && (
                <div>
                  <strong style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>Uploaded Date</strong>
                  <p style={{ marginTop: '0.25rem' }}>
                    {new Date(selectedPhotoDetails.uploaded_date).toLocaleDateString()}
                  </p>
                </div>
              )}

              {selectedPhotoDetails.consent_confirmation && (
                <div>
                  <strong style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>Consent Status</strong>
                  <p style={{ marginTop: '0.25rem', color: '#10b981', fontWeight: '600' }}>
                    ✓ Consent Confirmed
                  </p>
                </div>
              )}
            </div>

            <div style={{ marginTop: '1.5rem', display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => setSelectedPhotoDetails(null)}
              >
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={async () => {
                  const urlToOpen = imageUrlCache[selectedPhotoDetails.url] || await getSignedUrl(selectedPhotoDetails.url, 'ngo-documents');
                  window.open(urlToOpen, '_blank');
                }}
              >
                View Full Size
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Direct Impact Report Modal */}
      {showReportModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
          backdropFilter: 'blur(4px)'
        }} onClick={() => setShowReportModal(false)}>
          <div 
            className="card" 
            style={{ 
              width: '700px', maxHeight: '90vh', overflowY: 'auto', 
              padding: '2.5rem', position: 'relative', borderRadius: '16px',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' 
            }} 
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ fontSize: '1.75rem', marginBottom: '1.5rem', color: '#111827' }}>Submit Quarterly Impact Report</h2>
            
            <form onSubmit={handleReportSubmit}>
              <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>Select Project *</label>
                <select
                  value={selectedProjectForReport?.id || ''}
                  onChange={(e) => {
                    const project = projects.find(p => p.id === e.target.value);
                    const selProj = project || null;
                    setSelectedProjectForReport(selProj);
                    if (selProj) loadExistingReportValues(selProj);
                  }}
                  required
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #d1d5db' }}
                >
                  <option value="">Select project</option>
                  {projects.filter(p => p.implementation_type === 'direct' && p.status === 'active').map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>

              {selectedProjectForReport && (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
                    <div className="form-group">
                      <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>Quarter *</label>
                      <select
                        value={reportForm.quarter}
                        onChange={(e) => {
                          setReportForm({ ...reportForm, quarter: e.target.value });
                          loadExistingReportValues(null, e.target.value);
                        }}
                        required
                        style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #d1d5db' }}
                      >
                        <option value="Q1">Q1 (Apr-Jun)</option>
                        <option value="Q2">Q2 (Jul-Sep)</option>
                        <option value="Q3">Q3 (Oct-Dec)</option>
                        <option value="Q4">Q4 (Jan-Mar)</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>Year *</label>
                      <input
                        type="number"
                        value={reportForm.year}
                        onChange={(e) => {
                          const val = parseInt(e.target.value);
                          setReportForm({ ...reportForm, year: val });
                          loadExistingReportValues(null, undefined, val);
                        }}
                        required
                        style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #d1d5db' }}
                      />
                    </div>
                  </div>

                  <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                    <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>Period</label>
                    <input
                      type="text"
                      value={`${reportForm.quarter} FY ${reportForm.year}-${(reportForm.year + 1).toString().slice(-2)}`}
                      readOnly
                      style={{ width: '100%', padding: '0.75rem', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '8px', cursor: 'not-allowed' }}
                    />
                    <small style={{ color: '#6b7280', marginTop: '0.25rem', display: 'block' }}>Automatically generated from Quarter and Year</small>
                  </div>

                  {/* Metrics Section */}
                  <div style={{ marginBottom: '2rem' }}>
                    {metrics.filter(m => m.project_id === selectedProjectForReport.id).map((metric) => {
                      const achievedValue = parseFloat(reportForm.metric_updates[metric.id] || '0');
                      const targetValue = metric.target_value || 1;
                      const progress = Math.min(100, (achievedValue / targetValue) * 100);

                      return (
                        <div key={metric.id} className="card" style={{ marginBottom: '1rem', padding: '1.5rem', background: '#fff', border: '1px solid #e5e7eb' }}>
                          <h4 style={{ margin: '0 0 1rem 0' }}>{metric.metric_name} {metric.unit ? `(${metric.unit})` : ''}</h4>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1rem' }}>
                            <div className="form-group">
                              <label style={{ fontSize: '0.875rem', color: '#6b7280' }}>Target</label>
                              <input
                                type="number"
                                value={metric.target_value}
                                disabled
                                style={{ width: '100%', padding: '0.6rem', background: '#f9fafb', border: '1px solid #d1d5db', borderRadius: '6px' }}
                              />
                            </div>
                            <div className="form-group">
                              <label style={{ fontSize: '0.875rem', color: '#374151', fontWeight: '500' }}>Achieved Value *</label>
                              <input
                                type="number"
                                value={reportForm.metric_updates[metric.id] || ''}
                                onChange={(e) => setReportForm({
                                  ...reportForm,
                                  metric_updates: { ...reportForm.metric_updates, [metric.id]: e.target.value }
                                })}
                                placeholder="0"
                                required
                                style={{ width: '100%', padding: '0.6rem', border: '1px solid #4d905e', borderRadius: '6px' }}
                              />
                            </div>
                          </div>
                          
                          <div className="progress-container">
                            <label style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.4rem', display: 'block' }}>Progress</label>
                            <div style={{ height: '32px', background: '#f3f4f6', borderRadius: '6px', overflow: 'hidden', padding: '1px', border: '1px solid #e5e7eb' }}>
                              <div style={{ 
                                width: `${progress}%`, 
                                height: '100%', 
                                background: progress >= 100 ? '#10b981' : progress > 0 ? '#4d905e' : 'transparent',
                                display: 'flex', alignItems: 'center', paddingLeft: '0.5rem', color: progress > 10 ? '#fff' : '#ef4444',
                                fontSize: '0.75rem', fontWeight: '600', transition: 'width 0.3s ease'
                              }}>
                                {progress.toFixed(1)}% of target
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                    <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>Narrative Summary *</label>
                    <textarea 
                      rows={5} 
                      placeholder="Provide a comprehensive narrative summary of the impact achieved..."
                      value={reportForm.narrative_summary}
                      onChange={(e) => setReportForm({ ...reportForm, narrative_summary: e.target.value })}
                      required
                      style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #d1d5db' }}
                    />
                  </div>

                  <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                    <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>Photos</label>
                    <input 
                      type="file" 
                      multiple 
                      accept="image/*"
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        setReportForm({ ...reportForm, photos: [...reportForm.photos, ...files].slice(0, 10) });
                      }}
                      className="input"
                    />
                  </div>

                  <div className="form-group" style={{ marginBottom: '2rem' }}>
                    <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>Beneficiary Stories</label>
                    {beneficiaryStories.map((story, i) => (
                      <div key={i} style={{ padding: '1rem', background: '#f9fafb', borderRadius: '8px', marginBottom: '1rem', border: '1px solid #e5e7eb' }}>
                         <input 
                            placeholder="Beneficiary Name (Optional)" 
                            value={story.name || ''} 
                            onChange={(e) => {
                              const newStories = [...beneficiaryStories];
                              newStories[i].name = e.target.value;
                              setBeneficiaryStories(newStories);
                            }}
                            style={{ width: '100%', marginBottom: '0.5rem', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px' }}
                         />
                         <textarea 
                            placeholder="Share a story of impact..." 
                            value={story.story}
                            onChange={(e) => {
                              const newStories = [...beneficiaryStories];
                              newStories[i].story = e.target.value;
                              setBeneficiaryStories(newStories);
                            }}
                            style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px' }}
                         />
                      </div>
                    ))}
                    <button 
                      type="button" 
                      className="btn btn-link" 
                      onClick={() => setBeneficiaryStories([...beneficiaryStories, { story: '', name: '' }])}
                      style={{ color: '#4d905e', padding: 0 }}
                    >
                      + Add Story
                    </button>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '2rem' }}>
                    <button type="button" className="btn" onClick={() => setShowReportModal(false)} style={{ background: 'transparent', color: '#4d905e' }}>Cancel</button>
                    <button 
                      type="submit" 
                      className="btn" 
                      disabled={submittingReport}
                      style={{ background: '#4d905e', color: '#fff', padding: '0.75rem 2rem', borderRadius: '8px' }}
                    >
                      {submittingReport ? 'Submitting...' : 'Submit Report'}
                    </button>
                  </div>
                </>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyImpact;
